import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FS_VIEW_MAX, fsContent, fsDownload, type FsKind } from '../transport/api'
import { Markdown } from '../chat/Markdown'
import { Icon } from '../ui/Icon'
import { imageMime } from '../ui/ImageTile'
import { PDF_MIME, highlight, isPdf, mediaMime, parseDelimited, subKind, type Sub, type Tok } from './fileKinds'
import { prepareShareFile, saveLane, saveOutcomeNote, saveViaShare, shareFile } from './saveFile'

/**
 * File preview. Takes over the main area (same box as #term) rather than opening a
 * modal, so a file is read at full width like the terminal it replaces.
 *
 *   owner, 2026-08-07: text, code, csv, json, markdown, image, video, audio 都要能夠
 *   正確的依照該顯示的方式顯示. 該可以播放的方式播放.
 *
 * EVERY KIND WAS ALREADY BRANCHED FOR HERE AND NONE OF THEM COULD LOAD. The old
 * version pointed `<img>`, `<video>` and its text fetch at `/fs/file?path=…`, a raw
 * byte stream on the edge. This client never reaches the edge directly — every call is
 * an `http.request` command over the link — and from the browser's own origin
 * `/fs/file` answers 404 `no_such_route`. So did `/note/file` and `/project/file`: no
 * file content of any kind arrived, in the viewer or in chat. The branches rendered,
 * the bytes never came, and an empty `<img>` looks exactly like a broken image.
 *
 * Content now comes over the relay as JSON (`/fs/content`), text as text and bytes as
 * base64, and the bytes become an object URL so `<img>`, `<video>` and `<audio>` work
 * on a source the browser can actually reach.
 */

/** Media the browser will play from an object URL, once it has the bytes. */
const PLAYABLE = new Set<FsKind>(['image', 'video', 'audio'])

/**
 * Will this browser draw a PDF inside a page?
 *
 * `navigator.pdfViewerEnabled` is the browser's own answer (false on Chrome for Android,
 * and on a desktop browser whose viewer the person switched off). Where the property
 * does not exist the frame is tried: an engine too old to say is not one known to refuse.
 */
function showsPdf(): boolean {
  return (navigator as Navigator & { pdfViewerEnabled?: boolean }).pdfViewerEnabled !== false
}

/**
 * Bytes → an object URL the browser can point an element at, revoked on the way out.
 *
 * A `data:` URI would also render, and would put a multi-megabyte string in the DOM
 * and in every React devtools snapshot. An object URL is a handle; the cost of it is
 * that it leaks unless revoked, which is what the cleanup is for.
 */
function useObjectUrl(b64: string | undefined, mime: string | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!b64) { setUrl(null); return }
    const bin = atob(b64)
    const bytes = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
    const u = URL.createObjectURL(new Blob([bytes], mime ? { type: mime } : undefined))
    setUrl(u)
    return () => URL.revokeObjectURL(u)
  }, [b64, mime])
  return url
}

/** The same handle for bytes that arrived as a Blob already (the sliced road). */
function useBlobUrl(blob: Blob | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!blob) { setUrl(null); return }
    const u = URL.createObjectURL(blob)
    setUrl(u)
    return () => URL.revokeObjectURL(u)
  }, [blob])
  return url
}

function Table({ text, sep }: { text: string; sep: string }) {
  const rows = useMemo(() => parseDelimited(text, sep), [text, sep])
  const [head, ...body] = rows
  return (
    <div className="fv-table-wrap">
      <table className="fv-table">
        {head && (
          <thead>
            <tr>{head.map((c, i) => <th key={i}>{c}</th>)}</tr>
          </thead>
        )}
        <tbody>
          {body.map((r, i) => (
            <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>
          ))}
        </tbody>
      </table>
      {rows.length >= 2000 && <div className="fs-note">showing first 2000 rows</div>}
    </div>
  )
}

/*
 * LINE NUMBERS down the left of every text and code view — a user's request the owner
 * passed on, 2026-10-15: 使用者使用 file browser 在瀏覽純文字檔案或者是代碼的時候, 他希望
 * 左邊能夠出現每一行的行號數字. 這樣子它就更好, 更容易追蹤代碼.
 *
 * Each number is an EMPTY span at the start of its line, drawn by CSS from `data-n`
 * (shell.css `.fv-ln::before`), inside the same text as its line. Measured in Chromium
 * and in WebKit:
 *   - it sits in its line's own line box, so it cannot drift from it. A column of numbers
 *     beside the text would: a line holding a glyph from a fallback font (a Chinese
 *     comment, an emoji) is taller than one without, and the two columns part a little
 *     further at every such line;
 *   - it is generated content, so a selection — and so a copy — is the file's text and
 *     nothing else, in both engines (WebKit copies `user-select: none` text it spans);
 *   - a negative margin as wide as its box puts it in the gutter, so the line starts
 *     where it always did and a tab still reaches a whole tab stop;
 *   - it is an INLINE box, never an inline-block: one atomic box per line takes
 *     Chromium's layout of plain text off its fast path — a 2.4 MB ASCII log went from
 *     0.14 s to 17 s (the review of 2026-10-15). An inline box is as wide as its text, so
 *     every number is written to the same width (padded with spaces).
 *   - it is not sticky. Sticky numbers stayed at the left of a line scrolled sideways,
 *     and cost every scrolled frame (240 ms at 54,000 lines in Chromium); in Safari each
 *     sticky box in a scroller is likely a layer of its own — thousands on a phone.
 * The pre's text is still exactly what was shown before (textContent === text).
 */
function numberLines(toks: readonly Tok[]): { nodes: ReactNode[]; count: number } {
  const total = toks.reduce((n, t) => n + t.v.length, 0)
  // How many lines, first: every number is written to the width of the largest.
  let breaks = 0
  for (const t of toks) for (let k = t.v.indexOf('\n'); k !== -1; k = t.v.indexOf('\n', k + 1)) breaks += 1
  let closing = false
  for (const t of toks) if (t.v) closing = t.v.endsWith('\n')
  const width = String(total === 0 ? 0 : breaks + (closing ? 0 : 1)).length
  const nodes: ReactNode[] = []
  let count = 0
  let at = 0
  // Plain runs are gathered into one string: two nodes a line, not one per piece.
  let run = ''
  const flush = () => {
    if (run) nodes.push(run)
    run = ''
  }
  const number = () => {
    flush()
    count += 1
    nodes.push(<span key={`n${count}`} className="fv-ln" data-n={String(count).padStart(width)} aria-hidden="true" />)
  }
  if (total > 0) number()
  toks.forEach((tok, i) => {
    tok.v.split('\n').forEach((piece, j) => {
      if (j > 0) {
        run += '\n'
        at += 1
        // A file's closing newline ends its last line; it starts no new one.
        if (at < total) number()
      }
      if (!piece) return
      if (tok.t === 'txt') run += piece
      else {
        flush()
        nodes.push(<span key={`t${i}.${j}`} className={`hl-${tok.t}`}>{piece}</span>)
      }
      at += piece.length
    })
  })
  flush()
  return { nodes, count }
}

function Lined({ toks, className }: { toks: readonly Tok[]; className?: string }) {
  const { nodes, count } = useMemo(() => numberLines(toks), [toks])
  return (
    // The gutter is as wide as the largest number.
    <pre className={`fv-pre fv-lined${className ? ` ${className}` : ''}`} style={{ ['--ln-w' as string]: `${String(count).length}ch` }}>
      {nodes}
    </pre>
  )
}

/** Source, in five token classes. See `highlight` for why it is not a real parser. */
function Code({ text }: { text: string }) {
  const toks = useMemo(() => highlight(text), [text])
  return <Lined toks={toks} className="fv-code" />
}

/** Text as written. */
function Plain({ text }: { text: string }) {
  const toks = useMemo((): Tok[] => [{ t: 'txt', v: text }], [text])
  return <Lined toks={toks} />
}

/**
 * JSON, pretty-printed when it is one long line — and honest when it will not parse.
 *
 * The old version fell back to the raw string silently, so a malformed file looked
 * like one that simply had no indentation. The reason it did not parse is the single
 * most useful thing to say about a JSON file that does not parse.
 *
 * A file already written on lines of its own is shown AS WRITTEN, so its line numbers are
 * the file's: laid out again, `"files": ["dist", "src"]` became four lines and every
 * number after it pointed past the line a linter or an agent means by `package.json:12`
 * (the review of 2026-10-15). Only one long line — minified — is laid out to be read.
 */
function Json({ text }: { text: string }) {
  const parsed = useMemo(() => {
    try {
      const value: unknown = JSON.parse(text)
      return { ok: true as const, out: text.trim().includes('\n') ? text : JSON.stringify(value, null, 2) }
    } catch (e) {
      return { ok: false as const, why: (e as Error).message }
    }
  }, [text])
  return parsed.ok ? (
    <Code text={parsed.out} />
  ) : (
    <>
      <div className="fs-note fs-warn">not valid JSON — {parsed.why}; showing it as written</div>
      <Plain text={text} />
    </>
  )
}

/**
 * The Download control, immediately LEFT of Close (owner, 2026-09-13: 在 X 按鈕的左邊
 * 放置一個 Download 按鈕). A browser gets the anchor, whose `download` attribute is the
 * download. The Capacitor shell gets a button that opens the share sheet, because
 * WKWebView ignores that attribute — see saveFile.ts. Only offered when there is
 * something to point at: a download of a path this client cannot reach is a button
 * that produces a 404 page.
 */
function DownloadControl({
  src,
  name,
  kind,
  onNote,
}: {
  src: string
  name: string
  kind: FsKind
  onNote: (note: string | null) => void
}) {
  if (saveLane() === 'anchor') {
    return (
      <a className="fs-icon" href={src} download={name} title="Download" aria-label="Download">
        <Icon name="arrow_downward" size={15} label="Download" />
      </a>
    )
  }
  // The share sheet offers what the TYPE can do — "Save to Files", Books, Print for a PDF.
  const mime = kind === 'image' ? imageMime(name) : isPdf(name) ? PDF_MIME : ''
  return <ShareButton src={src} name={name} mime={mime} onNote={onNote} />
}

/**
 * The shell's Download. The File is PREPARED as soon as the panel shows the bytes,
 * so the tap hands it to navigator.share with nothing awaited in between — the
 * share sheet may only be asked for under the tap's own activation, and a fetch in
 * front of the ask can spend it (saveFile.ts). A tap that beats the prepare takes
 * the fetch-then-share road and, if that loses the activation, says to tap again —
 * by then the File is ready.
 */
function ShareButton({
  src,
  name,
  mime,
  onNote,
}: {
  src: string
  name: string
  mime: string
  onNote: (note: string | null) => void
}) {
  const ready = useRef<File | null>(null)
  useEffect(() => {
    let alive = true
    ready.current = null
    prepareShareFile(src, name, mime)
      .then((f) => {
        if (alive) ready.current = f
      })
      .catch(() => {
        /* the tap's own fetch will report it */
      })
    return () => {
      alive = false
    }
  }, [src, name, mime])
  return (
    <button
      type="button"
      className="fs-icon"
      title="Download"
      aria-label="Download"
      onClick={() => {
        const file = ready.current
        void (file ? shareFile(file, name) : saveViaShare(src, name, mime)).then((r) => onNote(saveOutcomeNote(r)))
      }}
    >
      <Icon name="arrow_downward" size={15} label="Download" />
    </button>
  )
}

export function FileViewer({
  path,
  url: urlOverride,
  name,
  kind,
  host,
  onClose,
}: {
  path?: string
  /** A URL the caller already has (project attachments). Skips the relay fetch. */
  url?: string
  name: string
  kind: FsKind
  /* `size` used to be passed so the viewer could refuse to fetch something huge. The
     edge decides that now — it knows the real size and the transport's ceiling, and a
     listing's size can be stale by the time anyone clicks. */
  /** The machine the file lives on — without it a remote's file is read from Origin. */
  host?: string | null
  onClose: () => void
}) {
  const sub: Sub = subKind(name)
  /*
   * A PDF — owner, 2026-10-12: 在 File Browser 里面打开 PDF, 好像没有预览的功能, 你把 PDF 预览的
   * 功能加进去. Shown by the BROWSER'S OWN viewer, in an <object>, from the same bytes every
   * other file here is shown from: no library is added for it (a PDF library carries its
   * own ciphers for password-protected files, and this bundle's cryptography is the
   * browser's alone). The edge calls a PDF `other`; the name says what it is.
   */
  const pdf = isPdf(name)
  /** What an element here can show once it has the bytes: a picture, a film, a sound, a PDF. */
  const showable = PLAYABLE.has(kind) || pdf

  const content = useQuery({
    queryKey: ['fs-content', path ?? '', host ?? ''],
    queryFn: () => fsContent(path ?? '', host),
    enabled: !urlOverride && !!path,
    /* A file does not change while it is being looked at, and re-fetching one over the
       relay costs a command per refocus. */
    staleTime: 60_000,
  })

  const c = content.data
  const blob = useObjectUrl(
    c?.ok && c.encoding === 'base64' ? c.data : undefined,
    /* A PDF's type is SAID, never taken from the machine: the element below shows whatever
       the type names, and bytes typed as a web page would run as one, in this origin. */
    pdf ? PDF_MIME : c?.mime,
  )
  /*
   * PAST 2.5 MB THE BYTES COME IN SLICES (owner, 2026-10-11: 放寬到五十個 MB). The one
   * message `/fs/content` answers in cannot grow — every hop has a ceiling on it — so a
   * picture or a film the edge refused as too_large is paged through the lane "Download
   * file" already uses, up to FS_VIEW_MAX. Only for what an element can then play: a
   * type with no preview is not fetched to be shown nothing.
   */
  const tooLarge = !!c && !c.ok && c.error === 'too_large'
  const paged = tooLarge && !!path && showable && typeof c.size === 'number' && c.size <= FS_VIEW_MAX
  const [pct, setPct] = useState(0)
  const slices = useQuery({
    queryKey: ['fs-slices', path ?? '', host ?? '', c?.size ?? 0],
    queryFn: ({ signal }) => {
      setPct(0)
      return fsDownload(path ?? '', host, setPct, { type: pdf ? PDF_MIME : mediaMime(name), signal })
    },
    enabled: paged,
    /* FETCHED ONCE PER VIEWING. A refocus or a reconnect must not page fifty megabytes
       again behind a film that is playing, and then swap the bytes under it — a new
       Blob is a new URL, and the player starts over. A file that really changed comes
       back through the small query above: its size is in this key. */
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    /* Tens of megabytes are not kept for a viewer that has closed. */
    gcTime: 0,
    retry: false,
  })
  const pagedUrl = useBlobUrl(paged ? slices.data : undefined)
  /*
   * A PDF THAT CAME AS A URL (a chat attachment) is fetched and re-typed too. A film
   * streams from its URL; a PDF pointed at a storage URL is a download as often as a
   * page — the header that says "attachment" decides, and it is not ours to set.
   */
  const remotePdf = useQuery({
    queryKey: ['url-pdf', urlOverride ?? ''],
    queryFn: async () => {
      const r = await fetch(urlOverride!)
      if (!r.ok) throw new Error(`the file answered ${r.status}`)
      return new Blob([await r.arrayBuffer()], { type: PDF_MIME })
    },
    enabled: !!urlOverride && pdf,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    gcTime: 0,
  })
  const remotePdfUrl = useBlobUrl(urlOverride && pdf ? remotePdf.data : undefined)
  const src = urlOverride && pdf ? remotePdfUrl : (urlOverride ?? blob ?? pagedUrl)
  /* The bytes arrived and the element refused them (a .mkv, a .heic outside Safari):
     a dead player says nothing, so the viewer does. */
  const [refused, setRefused] = useState(false)
  useEffect(() => setRefused(false), [src])
  /*
   * A URL-borne TEXT file must be READ, not just linked (owner via avg-main,
   * 2026-08-19: chat 裡 .md 附件點開是空白). Chat attachments arrive as
   * `urlOverride`, which disables the relay query above — so `text` stayed
   * undefined forever and only the PLAYABLE kinds (which hand `src` to an
   * element) ever showed anything. Fetch the bytes from the URL for the kinds
   * that render from text; the attachments bucket's CORS already allows GET
   * from this origin (the upload lane runs browser-side POSTs against it).
   */
  const wantsRemoteText = !!urlOverride && (kind === 'markdown' || kind === 'text')
  const remote = useQuery({
    queryKey: ['url-content', urlOverride ?? ''],
    queryFn: async () => {
      const r = await fetch(urlOverride!)
      if (!r.ok) throw new Error(`the file answered ${r.status}`)
      return r.text()
    },
    enabled: wantsRemoteText,
    staleTime: 60_000,
  })
  const text = c?.ok && c.encoding === 'utf8' ? c.data : remote.data
  /* What the shell's Download had to say — only the two outcomes a person must act
     on (saveFile.ts); a share that went through or was dismissed says nothing. */
  const [saveNote, setSaveNote] = useState<string | null>(null)

  return (
    <div id="fileView" aria-label={`Viewing ${name}`}>
      <div className="fv-hd">
        <Icon name="content_paste" size={15} />
        <span className="fv-name" title={path}>{name}</span>
        <span className="fv-meta">{pdf ? 'pdf' : kind}{sub !== 'plain' ? ` · ${sub}` : ''}</span>
        {src && <DownloadControl src={src} name={name} kind={kind} onNote={setSaveNote} />}
        <button type="button" className="fs-icon" onClick={onClose} title="Close" aria-label="Close">
          <Icon name="close" size={15} label="Close" />
        </button>
      </div>

      {/* fv-fit: a picture's frame — centred, capped by both sides (owner, 2026-09-13).
          Only for images: text scrolls, and a video sizes itself. */}
      <div className={`fv-body${kind === 'image' ? ' fv-fit' : ''}${pdf ? ' fv-doc' : ''}`}>
        {saveNote && <div className="fs-note fs-warn">{saveNote}</div>}
        {content.isPending && !urlOverride && <div className="fs-note">loading…</div>}
        {content.isError && (
          <div className="fs-note fs-err">couldn’t read this file — {String(content.error)}</div>
        )}
        {/* The URL lane's own states — `isLoading`, not `isPending`: a disabled
            query reports pending forever, and this one is disabled for every
            non-text kind. */}
        {(remote.isLoading || remotePdf.isLoading) && <div className="fs-note">loading…</div>}
        {(remote.isError || remotePdf.isError) && (
          <div className="fs-note fs-err">couldn’t read this file — {String(remote.error ?? remotePdf.error)}</div>
        )}

        {/*
          * TOO BIG IS ITS OWN ANSWER, with the numbers. The edge refuses rather than
          * truncating anything that is not text, because half a PNG and half an mp4
          * both look like a broken feature instead of a file that will not fit.
          */}
        {tooLarge && paged && slices.isLoading && (
          <div className="fs-note">
            loading… {pct}% of {(((c.size ?? 0) / 1_000_000)).toFixed(1)} MB
          </div>
        )}
        {tooLarge && paged && slices.isError && !slices.data && (
          <div className="fs-note fs-err">
            couldn’t read this file — {String((slices.error as Error)?.message ?? slices.error)}
          </div>
        )}
        {tooLarge && !paged && showable && (
          <div className="fs-note fs-warn">
            {(((c.size ?? 0) / 1_000_000)).toFixed(1)} MB is over the{' '}
            {(FS_VIEW_MAX / 1_000_000).toFixed(1)} MB this viewer opens — use Download file
            in the file list.
          </div>
        )}
        {tooLarge && !showable && (
          <div className="fs-note">No inline preview for this type — use Download file in the file list.</div>
        )}
        {c && !c.ok && c.error !== 'too_large' && (
          <div className="fs-note fs-err">couldn’t read this file — {c.error}</div>
        )}

        {c?.truncated && (
          <div className="fs-note fs-warn">
            showing the first {(((c.size ? Math.min(c.size, 2_500_000) : 0) / 1_000_000)).toFixed(1)} MB
            of {(((c.size ?? 0) / 1_000_000)).toFixed(1)} MB
          </div>
        )}

        {PLAYABLE.has(kind) && src && refused && (
          <div className="fs-note fs-warn">
            This browser can’t show this file — use Download file in the file list.
          </div>
        )}
        {PLAYABLE.has(kind) && src && !refused && (
          <>
            {kind === 'image' && <img className="fv-img" src={src} alt={name} onError={() => setRefused(true)} />}
            {/* no captions track exists for a file on someone's disk (fossil eslint-disable removed 2026-09-02) */}
            {kind === 'video' && <video className="fv-media" src={src} controls preload="metadata" onError={() => setRefused(true)} />}
            {/* no captions track exists for a file on someone's disk (fossil eslint-disable removed 2026-09-02) */}
            {kind === 'audio' && <audio className="fv-audio" src={src} controls preload="metadata" onError={() => setRefused(true)} />}
          </>
        )}

        {text !== undefined && (
          <>
            {kind === 'markdown' && (
              <div className="fv-md pmsg-body md"><Markdown body={text} /></div>
            )}
            {kind === 'text' && sub === 'json' && <Json text={text} />}
            {kind === 'text' && (sub === 'csv' || sub === 'tsv') && (
              <Table text={text} sep={sub === 'csv' ? ',' : '\t'} />
            )}
            {kind === 'text' && sub === 'code' && <Code text={text} />}
            {kind === 'text' && sub === 'plain' && <Plain text={text} />}
          </>
        )}

        {/*
          * THE BROWSER'S OWN PDF VIEWER, in a box that is the whole body: its scrolling,
          * its zoom, its search. The bytes are typed as a PDF above, so nothing in them
          * runs as a page.
          *
          * AN <object>, NOT AN <iframe> — and the difference is the whole of the phone.
          * Measured by avg-devapp-appstore in WKWebView (macOS 26.6, iOS 27.0, the same
          * three-page PDF from a blob typed as this one is), 2026-10-12:
          *     <iframe>            macOS: the whole document.   iOS: PAGE ONE ONLY, at the
          *                         PDF's own size, clipped at the right, no scrolling.
          *     <object> / <embed>  both: the whole document, fit to width, scrollable.
          * So the old rule about iOS holds only for a frame. What an <object> does not
          * give: a load event (on either platform — nothing here waits for one), and a
          * way in from the page (no contentDocument, no #page=N on iOS).
          *
          * ITS CHILDREN ARE WHAT IS SHOWN WHEN IT CANNOT DRAW, which is the one honest
          * signal there is; a browser that SAYS it shows no PDF inline (Chrome on Android)
          * is told the same thing without being asked to try.
          */}
        {pdf && src && showsPdf() && (
          <object className="fv-pdf" data={src} type={PDF_MIME} aria-label={name}>
            <div className="fs-note fs-warn">This browser can’t show a PDF here — use Download.</div>
          </object>
        )}
        {pdf && src && !showsPdf() && (
          <div className="fs-note fs-warn">This browser can’t show a PDF here — use Download.</div>
        )}

        {kind === 'other' && !pdf && c?.ok && (
          <div className="fs-note">No inline preview for this type — use Download.</div>
        )}
      </div>
    </div>
  )
}
